import { useRowSortable } from './lazySortable';
import type { Announcements } from '@dnd-kit/core';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { useTouchReveal } from '@/features/work-areas/workAreaDragHandle';
import {
  TOUCH_ONLY_MEDIA_QUERY,
  useMediaQuery,
  useTouchDesktopLayout,
} from '@/hooks/useMediaQuery';
import {
  flattenNamespaceHierarchy,
  type IWorkAreaOrderNamespaceNode,
} from '@/features/work-areas/workAreaHierarchy';
import type { WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { NamespaceGroupHeading } from './WorkAreaNamespaceTree';
import { reorderControls } from '@featit/shared/i18n/messages/en/reorderControls';
import { registerEnglishArea } from '@/i18n/catalogue';
import { englishT, useT } from '@/i18n/LocaleProvider';
import { pageT } from '@/i18n/pageTranslator';

registerEnglishArea('reorderControls', reorderControls);
import type { IWorkAreaNamespaceGroup } from './workAreaNamespaceGroups';

/**
 * F-0380: reordering the namespaces in the work-area selector, the way a work area already
 * reorders within one (`Layout.tsx`'s `handleWorkAreaDragEnd`/`SortableWorkAreaRow`). Kept in its
 * own file so the sidebar's drag machinery for namespaces stays a small, separate add rather than a
 * bigger diff inside `Layout.tsx` itself.
 *
 * Three ways to move a namespace among its siblings:
 * - **pointer drag** — the handle, shown from `sm` upward (same breakpoint docs/UX standards call
 *   "tablet and up") on a device with a precise pointer, hidden until hover/focus/touch like a
 *   work area's handle already is (`.work-area-drag-handle`, reused rather than a second CSS
 *   rule);
 * - **keyboard** — the same handle, focused and moved with dnd-kit's `KeyboardSensor`; every move
 *   is announced with the namespace's name and its new position (`namespaceDragAnnouncements`);
 * - **move up/down buttons** — shown below `sm`, *and* on any coarse-pointer device regardless of
 *   width (`TOUCH_ONLY_MEDIA_QUERY`, the hook `PendingWorkItemAttachmentGallery.tsx` already uses
 *   for the same "(pointer: coarse) and (hover: none)" test), instead of drag: a heading is a
 *   wide, content-bearing row, and dragging one with a finger is a worse affordance than two
 *   buttons are — true at 390px and just as true on a touch tablet at 700px, which the original
 *   `sm`-only cutoff missed (a touch tablet between 640 and 768 had drag only, flagged by
 *   team-lead and fixed here). The drag handle and the buttons are mutually exclusive per device:
 *   a hybrid laptop (touch *and* a trackpad, `hover: hover`) keeps drag, since it is excluded from
 *   `TOUCH_ONLY_MEDIA_QUERY` on purpose (I-1044).
 *
 * Only an organisation or a subgroup reorders. The reader's own personal namespace is always first
 * and is never a candidate (there is only ever one of it); a fallback "other" group — a namespace
 * the tree does not list, headed by what a work area says about it — is not a real, editable
 * namespace the reader can move either.
 */

export type NamespaceMoveDirection = 'up' | 'down';

/**
 * The namespace immediately before/after `namespaceId` among its own siblings: what the phone's
 * move button needs, and what tells a move button whether it has anywhere left to go.
 *
 * Reuses `flattenNamespaceHierarchy` for the namespace ordering itself — the same function
 * `moveNamespaceWithinParent` (`@featit/shared`) already builds on — rather than a second walk of
 * the namespace tree with its own idea of "siblings".
 */
export function namespaceMoveTarget(
  namespaces: readonly IWorkAreaOrderNamespaceNode[],
  namespaceOrder: readonly string[],
  namespaceId: string,
  direction: NamespaceMoveDirection,
): string | null {
  const groupNamespaces = namespaces.filter((namespace) => namespace.kind !== 'user');
  const byId = new Map(groupNamespaces.map((namespace) => [namespace.id, namespace]));
  const active = byId.get(namespaceId);
  if (!active) return null;
  const parentKey = (namespace: IWorkAreaOrderNamespaceNode): string | null =>
    !namespace.parentId || namespace.parentId === namespace.id || !byId.has(namespace.parentId)
      ? null
      : namespace.parentId;
  const orderedIds = flattenNamespaceHierarchy(namespaces, [...namespaceOrder]).map(
    (row) => row.project.id,
  );
  const siblingIds = orderedIds.filter((id) => parentKey(byId.get(id)!) === parentKey(active));
  const index = siblingIds.indexOf(namespaceId);
  if (index < 0) return null;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  return targetIndex >= 0 && targetIndex < siblingIds.length ? siblingIds[targetIndex] : null;
}

/**
 * I-1263 (AC-18.11): the work area immediately before/after `projectId` among its own siblings in
 * one namespace's rows — what a work area's move arrow needs, the twin of `namespaceMoveTarget`.
 * Siblings share a parent work area (`parentId`), or have none in this list; the move itself is
 * `moveWorkAreaWithinParent`, the rule a drag uses.
 */
export function workAreaMoveTarget(
  rows: readonly { project: { id: string; parentId?: string | null } }[],
  projectId: string,
  direction: NamespaceMoveDirection,
): string | null {
  const ids = new Set(rows.map(({ project }) => project.id));
  const parentOf = (project: { id: string; parentId?: string | null }): string | null =>
    project.parentId && project.parentId !== project.id && ids.has(project.parentId)
      ? project.parentId
      : null;
  const active = rows.find(({ project }) => project.id === projectId)?.project;
  if (!active) return null;
  const siblings = rows
    .map(({ project }) => project)
    .filter((project) => parentOf(project) === parentOf(active))
    .map((project) => project.id);
  const index = siblings.indexOf(projectId);
  const target = direction === 'up' ? index - 1 : index + 1;
  return target >= 0 && target < siblings.length ? siblings[target] : null;
}

const COMPACT_ARROW =
  'grid h-6 w-6 place-items-center rounded text-gray-400 outline-none hover:bg-[#E4E6E8] hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-default disabled:opacity-35 dark:text-gray-500 dark:hover:bg-[#1f1f1f] dark:hover:text-gray-100';

const MOVE_ARROW =
  'grid h-touch w-touch shrink-0 place-items-center rounded text-gray-400 outline-none hover:bg-[#E4E6E8] hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-default disabled:opacity-35 dark:text-gray-500 dark:hover:bg-[#1f1f1f] dark:hover:text-gray-100';

/**
 * I-1263 (AC-18.11): the move-up and move-down pair, the last controls of an organisation's heading
 * and of a work area's row, so both pairs stand in one right-aligned column. Shown where a finger
 * reorders (below `sm`, or a touch-only device at any width — F-0380's rule); a precise pointer
 * keeps the drag handle instead. 44 × 44 px each, labelled with what they move.
 */
export function MoveArrows({
  name,
  touchOnly,
  disabled,
  hasRoomAbove,
  hasRoomBelow,
  onMove,
  className = '',
}: {
  name: string;
  touchOnly: boolean;
  /** While the order is unread or saving: a state of the list, not a permission. */
  disabled: boolean;
  /**
   * Whether there is a sibling to swap with — the row's place, not an authority, so an arrow at an
   * edge is natively `disabled` (F-0324 is about permissions; `disableNotHide.guard` rule 4).
   */
  hasRoomAbove: boolean;
  hasRoomBelow: boolean;
  onMove: (direction: NamespaceMoveDirection) => void;
  /** Lines the pair up with the other rows' pairs, where a row's own end padding differs. */
  className?: string;
}) {
  const t = useT();
  return (
    <span
      data-move-arrows=""
      className={`${touchOnly ? 'flex' : 'flex sm:hidden'} shrink-0 items-center ${className}`}
    >
      <button
        type="button"
        aria-label={t('reorderControls.moveUp', { name })}
        title={t('reorderControls.moveUpTitle')}
        disabled={disabled || !hasRoomAbove}
        onClick={(event) => {
          event.stopPropagation();
          onMove('up');
        }}
        className={MOVE_ARROW}
      >
        <ChevronUp className="h-4 w-4 touch-glyph" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={t('reorderControls.moveDown', { name })}
        title={t('reorderControls.moveDownTitle')}
        disabled={disabled || !hasRoomBelow}
        onClick={(event) => {
          event.stopPropagation();
          onMove('down');
        }}
        className={MOVE_ARROW}
      >
        <ChevronDown className="h-4 w-4 touch-glyph" aria-hidden="true" />
      </button>
    </span>
  );
}

/**
 * dnd-kit's `KeyboardSensor` announces every pick-up/move/drop through its own live region by
 * default; this only replaces the wording so it names the namespace and the position it landed on
 * instead of a generic "draggable item" — F-0380's "an announced position".
 *
 * `orderedIds` is the list as rendered when the drag started: dnd-kit does not reorder the
 * `SortableContext`'s own `items` until the drop is applied, so `over`'s index in that list is the
 * same approximation of "where it would land" the library's own examples announce by.
 */
export function namespaceDragAnnouncements(
  orderedIds: readonly string[],
  nameOf: (namespaceId: string) => string,
): Announcements {
  // I-1976: said when dnd-kit calls it, outside a render, so in the page's language (`pageT`).
  const t = () => pageT(englishT);
  const position = (id: string): { position: number; count: number } | null => {
    const index = orderedIds.indexOf(id);
    return index < 0 ? null : { position: index + 1, count: orderedIds.length };
  };
  return {
    onDragStart({ active }) {
      return t()('reorder.pickedUp', { name: nameOf(String(active.id)) });
    },
    onDragOver({ active, over }) {
      const name = nameOf(String(active.id));
      if (!over) return t()('reorder.namespaces.notOver', { name });
      if (String(over.id) === String(active.id)) return undefined;
      const at = position(String(over.id));
      return at
        ? t()('reorder.namespaces.movedToPosition', { name, ...at })
        : t()('reorder.namespaces.moved', { name });
    },
    onDragEnd({ active, over }) {
      const name = nameOf(String(active.id));
      const at = over ? position(String(over.id)) : null;
      return at
        ? t()('reorder.namespaces.droppedAt', { name, ...at })
        : t()('reorder.namespaces.droppedHere', { name });
    },
    onDragCancel({ active }) {
      return t()('reorder.namespaces.cancelled', { name: nameOf(String(active.id)) });
    },
  };
}

export interface SortableNamespaceHeadingProps {
  group: IWorkAreaNamespaceGroup;
  /** Only an organisation or a subgroup reorders — see the module doc above. */
  reorderable: boolean;
  reorderDisabled: boolean;
  touchHandleVisible: boolean;
  onTouchReveal: (key: string) => void;
  hasNamespaceAbove: boolean;
  hasNamespaceBelow: boolean;
  onMove: (namespaceId: string, direction: NamespaceMoveDirection) => void;
  onCreateWorkArea?: () => void;
  onCreateSubgroup?: () => void;
  /** F-0416: the phone picker's edit mode — the heading's actions show without hover or focus. */
  actionsVisible?: boolean;
  /** Governs both actions above (SB-3) — see `NamespaceGroupHeading`'s `gate`. */
  gate?: WorkAreaGateType;
  /**
   * Whether `projectStore.selectedNamespaceId` is this namespace (SB-2): ORed with
   * `touchHandleVisible` into the one `touchRevealed` signal `NamespaceGroupHeading` reads, so a
   * touch device reveals the action icons on the selected row even before anything is tapped.
   */
  selected?: boolean;
  /** Scopes the app to this namespace (F-0383, §10.9) — see `NamespaceGroupHeading`. */
  onSelectNamespace?: () => void;
}

export function SortableNamespaceHeading({
  group,
  reorderable,
  reorderDisabled,
  touchHandleVisible,
  onTouchReveal,
  hasNamespaceAbove,
  hasNamespaceBelow,
  onMove,
  onCreateWorkArea,
  onCreateSubgroup,
  actionsVisible = false,
  gate,
  selected = false,
  onSelectNamespace,
}: SortableNamespaceHeadingProps) {
  const t = useT();
  const sortableId = group.namespaceId ?? group.key;
  const { attributes, listeners, setNodeRef, transformCss, transition, isDragging } =
    useRowSortable({ id: sortableId, disabled: reorderDisabled || !reorderable });
  // Follow-up to F-0380 (team-lead, 2026-10-02): a touch tablet between 640 and 768px matched
  // `sm:grid` (the drag handle) and not `sm:hidden` (the buttons), leaving it drag-only. A coarse
  // pointer with no hover gets the buttons at every width, not only below `sm` — see the module
  // doc above.
  const touchOnly = useMediaQuery(TOUCH_ONLY_MEDIA_QUERY);
  // U-0024 (IP-27): on the iPad's sidebar the moves are in the heading's ⋯, not on a line below it.
  const touchMenu = useTouchDesktopLayout() && !actionsVisible;
  // SB-2: selected or (for a reorderable heading) tapped open — never every row at once.
  const touchRevealed = touchHandleVisible || selected;
  const touchReveal = useTouchReveal(() => onTouchReveal(group.key));

  if (!reorderable) {
    return (
      <NamespaceGroupHeading
        group={group}
        onCreateWorkArea={onCreateWorkArea}
        onCreateSubgroup={onCreateSubgroup}
        actionsVisible={actionsVisible}
        gate={gate}
        touchRevealed={touchRevealed}
        onSelectNamespace={onSelectNamespace}
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={isDragging ? 'relative z-10 opacity-70' : ''}
      style={{ transform: transformCss, transition }}
      // I-1074: the handle shows after the tap, not while the finger is down (`useTouchReveal`).
      onPointerDown={touchReveal.onPointerDown}
      onClick={touchReveal.onClick}
    >
      <NamespaceGroupHeading
        group={group}
        onCreateWorkArea={onCreateWorkArea}
        onCreateSubgroup={onCreateSubgroup}
        actionsVisible={actionsVisible}
        gate={gate}
        touchRevealed={touchRevealed}
        onSelectNamespace={onSelectNamespace}
        moves={{
          up: hasNamespaceAbove ? () => onMove(sortableId, 'up') : null,
          down: hasNamespaceBelow ? () => onMove(sortableId, 'down') : null,
          disabled: reorderDisabled,
        }}
        /*
          I-1263 (AC-18.11, Jay: "remove the label and line and move the up and down arrows to the
          other organisation icons right aligned"): in the edit mode the arrows are the heading's
          last controls, in the work areas' arrow column — no line of their own.
        */
        trailing={
          actionsVisible ? (
            <MoveArrows
              name={group.name}
              touchOnly={touchOnly}
              disabled={reorderDisabled}
              hasRoomAbove={hasNamespaceAbove}
              hasRoomBelow={hasNamespaceBelow}
              onMove={(direction) => onMove(sortableId, direction)}
            />
          ) : undefined
        }
        leading={
          /*
            Drawn over the namespace's icon, inside the heading's own `.group`: the handle shows
            where the icon was and takes no room in the row (`index.css`, `.namespace-leading`).
            Follow-up to F-0380 (team-lead, 2026-10-02): a touch tablet between 640 and 768px
            matched `sm:grid` and not the move buttons' `sm:hidden`, leaving it drag-only — a
            coarse pointer with no hover gets the buttons at every width, not only below `sm`, so
            the handle itself must stay hidden at every width on that device (see the module doc
            above).
          */
          <button
            type="button"
            aria-label={t('reorderControls.reorderHandle', { name: group.name })}
            title={t('reorderControls.dragTitle')}
            disabled={reorderDisabled}
            className={`work-area-drag-handle absolute left-1/2 top-1/2 hidden h-6 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab place-items-center rounded text-gray-400 outline-none hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 active:cursor-grabbing disabled:cursor-default disabled:opacity-35 ${touchOnly ? '' : 'sm:grid'} dark:text-gray-500 dark:hover:text-gray-200 ${
              touchHandleVisible || isDragging ? 'work-area-drag-handle--visible' : ''
            }`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3 w-3" aria-hidden="true" />
          </button>
        }
      />
      {/*
        F-0380: outside the edit mode (the sidebar on a touch-only device, below `sm`) the arrows keep
        a line of their own, right-aligned, with no label: the sidebar is narrow, and its heading
        still holds the actions that show on a tap — two 44 px arrows inline left the name no room
        at all (measured on the I-1263 lane, 1280 touch: "Acme" drawn as nothing). On the iPad's
        sidebar (a finger from 768 px, U-0024, IP-27) the moves are in the heading's ⋯ instead, and
        this line is not drawn. Not `role="group"`: the heading's enclosing `<div role="group" aria-label={group.name}>`
        (`Layout.tsx`) is already a group of this namespace's own work areas.
      */}
      {actionsVisible || touchMenu ? null : (
        <div
          className={`flex items-center justify-end gap-0.5 pb-0.5 ${touchOnly ? '' : 'sm:hidden'}`}
        >
          <button
            type="button"
            aria-label={t('reorderControls.moveUp', { name: group.name })}
            title={t('reorderControls.moveUpTitle')}
            disabled={reorderDisabled || !hasNamespaceAbove}
            onClick={() => onMove(sortableId, 'up')}
            className={COMPACT_ARROW}
          >
            <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('reorderControls.moveDown', { name: group.name })}
            title={t('reorderControls.moveDownTitle')}
            disabled={reorderDisabled || !hasNamespaceBelow}
            onClick={() => onMove(sortableId, 'down')}
            className={COMPACT_ARROW}
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
